import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { OBJECTIVE_TYPE_OPTIONS } from '@jeraisonne/db/objectives';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useCreateObjectiveTemplate } from '@/lib/admin/objective-mutations';
import { localInputToIso } from '@/lib/student-objectives/deadline-format';

const OBJECTIVE_TYPES = OBJECTIVE_TYPE_OPTIONS;

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2 w-full';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

export function TutorAdminObjectivesNewPage() {
  const navigate = useNavigate();
  const create = useCreateObjectiveTemplate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<string>('full_program');
  const [contentType, setContentType] = useState<'exercises' | 'epreuves'>('exercises');
  const [subject, setSubject] = useState<SubjectSlug | ''>('');
  const [allowedLevels, setAllowedLevels] = useState<SchoolLevel[]>([]);
  const [requiresPackSelection, setRequiresPackSelection] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState(10);
  // datetime-local : "YYYY-MM-DDTHH:mm". Vide = pas de deadline par défaut.
  const [defaultDeadline, setDefaultDeadline] = useState('');

  function toggleLevel(level: SchoolLevel) {
    setAllowedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const result = await create.mutateAsync({
      title: title.trim(),
      description: description.trim() || undefined,
      type,
      content_type: contentType,
      subject: subject || null,
      allowed_levels: allowedLevels,
      requires_pack_selection: requiresPackSelection,
      is_active: isActive,
      display_order: displayOrder,
      default_deadline: localInputToIso(defaultDeadline),
    });
    navigate({ to: '/tutor/admin/objectives/$objectiveId', params: { objectiveId: result.id } });
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/objectives"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Objectifs
      </Link>

      <h1 className="font-display text-ink-900 mb-6 text-[22px] font-extrabold">
        Nouveau template d'objectif
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Titre */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="ex. Réviser un chapitre"
            className={inputCls}
            required
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Description <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Brève description pour l'élève..."
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {/* Type */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          >
            {OBJECTIVE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Content type */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Type de contenu</label>
          <div className="flex gap-3">
            {(['exercises', 'epreuves'] as const).map((ct) => (
              <label key={ct} className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="contentType"
                  value={ct}
                  checked={contentType === ct}
                  onChange={() => setContentType(ct)}
                  className="accent-primary-600"
                />
                <span className="font-sans text-[14px]">
                  {ct === 'exercises' ? 'Exercices' : 'Épreuves'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Matière */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Matière <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value as SubjectSlug | '')}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          >
            <option value="">Toutes les matières</option>
            {SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Niveaux autorisés */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Niveaux <span className="text-ink-400 font-normal">(vide = tous les niveaux)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {SCHOOL_LEVELS.map((l) => (
              <label key={l.value} className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={allowedLevels.includes(l.value as SchoolLevel)}
                  onChange={() => toggleLevel(l.value as SchoolLevel)}
                  className="accent-primary-600"
                />
                <span className="font-sans text-[13px]">{l.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Deadline par défaut */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Date butoir par défaut{' '}
            <span className="text-ink-400 font-normal">
              (recommandé pour les épreuves — 08h00 par défaut)
            </span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={defaultDeadline}
              onChange={(e) => setDefaultDeadline(e.target.value)}
              className={inputCls}
            />
            {defaultDeadline && (
              <button
                type="button"
                onClick={() => setDefaultDeadline('')}
                className="text-ink-500 hover:text-ink-700 font-sans text-[12px] underline"
              >
                Effacer
              </button>
            )}
          </div>
          <p className="text-ink-400 font-sans text-[11px]">
            Pré-remplie sur l'objectif élève ; l'élève peut la modifier.
          </p>
        </div>

        {/* Options toggles */}
        <div className="border-ink-100 space-y-3 rounded-xl border p-4">
          {(
            [
              {
                id: 'pack',
                label: 'L’élève choisit un pack',
                value: requiresPackSelection,
                set: setRequiresPackSelection,
              },
              {
                id: 'active',
                label: 'Actif (visible pour les élèves)',
                value: isActive,
                set: setIsActive,
              },
            ] as { id: string; label: string; value: boolean; set: (v: boolean) => void }[]
          ).map(({ id, label, value, set }) => (
            <label key={id} className="flex cursor-pointer items-center justify-between">
              <span className="font-sans text-[14px]">{label}</span>
              <button
                type="button"
                role="switch"
                aria-checked={value}
                onClick={() => set(!value)}
                className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                  value ? 'bg-primary-600' : 'bg-ink-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 translate-x-1 rounded-full bg-white shadow transition-transform ${
                    value ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </label>
          ))}
        </div>

        {/* Ordre d'affichage */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Ordre d'affichage</label>
          <input
            type="number"
            min={0}
            value={displayOrder}
            onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
            className={inputCls}
          />
        </div>

        <p className="text-ink-400 font-sans text-[12px]">
          Après la création, tu pourras épingler un programme, des packs ou des SKILLCARDS sur cet
          objectif ; sinon la règle du type s’applique.
        </p>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Link to="/tutor/admin/objectives" className="flex-1">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button type="submit" className="flex-1" disabled={create.isPending || !title.trim()}>
            {create.isPending ? 'Création...' : 'Créer'}
          </Button>
        </div>

        {create.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{create.error?.message}</p>
        )}
      </form>
    </div>
  );
}
